<script setup>
import { ref, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'

const isOpen = ref(false)

// Feature states
const fontSize = ref(0) // 0=normal, 1=large, 2=largest
const highContrast = ref(false)
const linkHighlight = ref(false)
const grayscale = ref(false)
const readableFont = ref(false)
const largeCursor = ref(false)
const stopAnimations = ref(false)
const lineHeight = ref(false)
const letterSpacing = ref(false)
const textAlign = ref(false)

const router = useRouter()

const ALL_CLASSES = [
  'a11y-font-1',
  'a11y-font-2',
  'a11y-high-contrast',
  'a11y-links',
  'a11y-grayscale',
  'a11y-readable-font',
  'a11y-large-cursor',
  'a11y-stop-animations',
  'a11y-line-height',
  'a11y-letter-spacing',
  'a11y-text-align',
]

function toggleClass(className, active) {
  document.documentElement.classList.toggle(className, active)
}

function removeAllClasses() {
  ALL_CLASSES.forEach((c) => document.documentElement.classList.remove(c))
}

function cycleFontSize() {
  // Remove current font classes
  toggleClass('a11y-font-1', false)
  toggleClass('a11y-font-2', false)

  fontSize.value = (fontSize.value + 1) % 3
  if (fontSize.value === 1) toggleClass('a11y-font-1', true)
  if (fontSize.value === 2) toggleClass('a11y-font-2', true)
}

function toggle(feature) {
  switch (feature) {
    case 'highContrast':
      highContrast.value = !highContrast.value
      toggleClass('a11y-high-contrast', highContrast.value)
      break
    case 'linkHighlight':
      linkHighlight.value = !linkHighlight.value
      toggleClass('a11y-links', linkHighlight.value)
      break
    case 'grayscale':
      grayscale.value = !grayscale.value
      toggleClass('a11y-grayscale', grayscale.value)
      break
    case 'readableFont':
      readableFont.value = !readableFont.value
      toggleClass('a11y-readable-font', readableFont.value)
      break
    case 'largeCursor':
      largeCursor.value = !largeCursor.value
      toggleClass('a11y-large-cursor', largeCursor.value)
      break
    case 'stopAnimations':
      stopAnimations.value = !stopAnimations.value
      toggleClass('a11y-stop-animations', stopAnimations.value)
      break
    case 'lineHeight':
      lineHeight.value = !lineHeight.value
      toggleClass('a11y-line-height', lineHeight.value)
      break
    case 'letterSpacing':
      letterSpacing.value = !letterSpacing.value
      toggleClass('a11y-letter-spacing', letterSpacing.value)
      break
    case 'textAlign':
      textAlign.value = !textAlign.value
      toggleClass('a11y-text-align', textAlign.value)
      break
  }
}

function resetAll() {
  removeAllClasses()
  fontSize.value = 0
  highContrast.value = false
  linkHighlight.value = false
  grayscale.value = false
  readableFont.value = false
  largeCursor.value = false
  stopAnimations.value = false
  lineHeight.value = false
  letterSpacing.value = false
  textAlign.value = false
}

function goToStatement() {
  isOpen.value = false
  router.push('/accessibility')
}

const fontSizeLabel = () => {
  if (fontSize.value === 0) return 'רגיל'
  if (fontSize.value === 1) return 'גדול'
  return 'גדול מאוד'
}

// Cleanup on unmount
onBeforeUnmount(() => {
  removeAllClasses()
})
</script>

<template>
  <!-- Backdrop -->
  <Transition name="a11y-fade">
    <div v-if="isOpen" class="a11y-backdrop" @click="isOpen = false" />
  </Transition>

  <div class="a11y-widget">
    <!-- Floating Trigger Button -->
    <button
      class="a11y-trigger"
      :class="{ 'is-open': isOpen }"
      :aria-label="isOpen ? 'סגור תפריט נגישות' : 'פתח תפריט נגישות'"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        width="24"
        height="24"
        aria-hidden="true"
      >
        <circle cx="12" cy="4" r="2" />
        <path
          d="M19 13v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.01 0-.01-.01-.02-.01H13c-.35-.2-.75-.3-1.19-.26C10.76 7.11 10 8.04 10 9.09V15c0 1.1.9 2 2 2h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29 1.07 3.25 1.94 5 1.95zm-6.17 5c-.41 1.16-1.52 2-2.83 2-1.66 0-3-1.34-3-3 0-1.31.84-2.41 2-2.83V12.1c-2.28.46-4 2.48-4 4.9 0 2.76 2.24 5 5 5 2.42 0 4.44-1.72 4.9-4h-2.07z"
        />
      </svg>
      <span class="a11y-trigger-tooltip">נגישות</span>
    </button>

    <!-- Slide-out Panel -->
    <Transition name="a11y-slide">
      <div v-if="isOpen" class="a11y-panel" role="dialog" aria-label="הגדרות נגישות">
        <div class="a11y-panel-header">
          <h3 class="a11y-panel-title">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="20"
              height="20"
              aria-hidden="true"
            >
              <circle cx="12" cy="4" r="2" />
              <path
                d="M19 13v-2c-1.54.02-3.09-.75-4.07-1.83l-1.29-1.43c-.17-.19-.38-.34-.61-.45-.01 0-.01-.01-.02-.01H13c-.35-.2-.75-.3-1.19-.26C10.76 7.11 10 8.04 10 9.09V15c0 1.1.9 2 2 2h5v5h2v-5.5c0-1.1-.9-2-2-2h-3v-3.45c1.29 1.07 3.25 1.94 5 1.95zm-6.17 5c-.41 1.16-1.52 2-2.83 2-1.66 0-3-1.34-3-3 0-1.31.84-2.41 2-2.83V12.1c-2.28.46-4 2.48-4 4.9 0 2.76 2.24 5 5 5 2.42 0 4.44-1.72 4.9-4h-2.07z"
              />
            </svg>
            הגדרות נגישות
          </h3>
          <button class="a11y-close" aria-label="סגור" @click="isOpen = false">✕</button>
        </div>

        <div class="a11y-panel-body">
          <!-- Font Size -->
          <button class="a11y-option" :class="{ active: fontSize > 0 }" @click="cycleFontSize">
            <span class="a11y-option-icon">א+</span>
            <span class="a11y-option-label">גודל טקסט</span>
            <span class="a11y-option-status">{{ fontSizeLabel() }}</span>
          </button>

          <!-- High Contrast -->
          <button
            class="a11y-option"
            :class="{ active: highContrast }"
            @click="toggle('highContrast')"
          >
            <span class="a11y-option-icon">◑</span>
            <span class="a11y-option-label">ניגודיות גבוהה</span>
          </button>

          <!-- Link Highlighting -->
          <button
            class="a11y-option"
            :class="{ active: linkHighlight }"
            @click="toggle('linkHighlight')"
          >
            <span class="a11y-option-icon">🔗</span>
            <span class="a11y-option-label">הדגשת קישורים</span>
          </button>

          <!-- Grayscale -->
          <button class="a11y-option" :class="{ active: grayscale }" @click="toggle('grayscale')">
            <span class="a11y-option-icon">◐</span>
            <span class="a11y-option-label">גווני אפור</span>
          </button>

          <!-- Readable Font -->
          <button
            class="a11y-option"
            :class="{ active: readableFont }"
            @click="toggle('readableFont')"
          >
            <span class="a11y-option-icon">Aa</span>
            <span class="a11y-option-label">גופן קריא</span>
          </button>

          <!-- Large Cursor -->
          <button
            class="a11y-option"
            :class="{ active: largeCursor }"
            @click="toggle('largeCursor')"
          >
            <span class="a11y-option-icon">▶</span>
            <span class="a11y-option-label">סמן גדול</span>
          </button>

          <!-- Stop Animations -->
          <button
            class="a11y-option"
            :class="{ active: stopAnimations }"
            @click="toggle('stopAnimations')"
          >
            <span class="a11y-option-icon">⏸</span>
            <span class="a11y-option-label">עצירת אנימציות</span>
          </button>

          <!-- Line Height -->
          <button class="a11y-option" :class="{ active: lineHeight }" @click="toggle('lineHeight')">
            <span class="a11y-option-icon">↕</span>
            <span class="a11y-option-label">מרווח שורות</span>
          </button>

          <!-- Letter Spacing -->
          <button
            class="a11y-option"
            :class="{ active: letterSpacing }"
            @click="toggle('letterSpacing')"
          >
            <span class="a11y-option-icon">A‥B</span>
            <span class="a11y-option-label">מרווח אותיות</span>
          </button>

          <!-- Text Alignment -->
          <button class="a11y-option" :class="{ active: textAlign }" @click="toggle('textAlign')">
            <span class="a11y-option-icon">☰</span>
            <span class="a11y-option-label">יישור לימין</span>
          </button>
        </div>

        <div class="a11y-panel-footer">
          <button class="a11y-reset" @click="resetAll">איפוס הגדרות</button>
          <button class="a11y-statement-link" @click="goToStatement">הצהרת נגישות מלאה</button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.a11y-widget {
  position: fixed;
  z-index: 9000;
  top: 0;
  left: 0;
  height: 0;
  width: 0;
}

/* Trigger Button */
.a11y-trigger {
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
  background: var(--gradient-primary);
  color: var(--color-white);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 2px 12px rgba(30, 41, 59, 0.3);
  transition: all var(--transition-base);
  z-index: 9001;
}

.a11y-trigger:hover {
  width: 56px;
  background: var(--color-primary-hover);
  box-shadow: 4px 2px 16px rgba(30, 41, 59, 0.4);
}

.a11y-trigger.is-open {
  opacity: 0;
  pointer-events: none;
}

.a11y-trigger:focus-visible {
  outline: 3px solid var(--color-info);
  outline-offset: 2px;
}

.a11y-trigger-tooltip {
  position: absolute;
  right: -8px;
  transform: translateX(100%);
  background: var(--color-gray-900);
  color: var(--color-white);
  font-size: var(--font-size-xs);
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--transition-fast);
}

.a11y-trigger:hover .a11y-trigger-tooltip {
  opacity: 1;
}

/* Backdrop */
.a11y-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 8999;
}

/* Panel */
.a11y-panel {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 320px;
  max-width: 100vw;
  background: var(--bg-primary);
  box-shadow: var(--shadow-xl);
  z-index: 9002;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.a11y-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--gradient-primary);
  color: var(--color-white);
  flex-shrink: 0;
}

.a11y-panel-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-lg);
  font-weight: 600;
  margin: 0;
}

.a11y-close {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: var(--color-white);
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-full);
  cursor: pointer;
  font-size: var(--font-size-base);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--transition-fast);
}

.a11y-close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.a11y-close:focus-visible {
  outline: 2px solid var(--color-white);
  outline-offset: 2px;
}

/* Panel Body */
.a11y-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

/* Option Buttons */
.a11y-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: right;
  font-family: inherit;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.a11y-option:hover {
  background: var(--bg-tertiary);
  border-color: var(--color-primary-light);
}

.a11y-option:focus-visible {
  outline: 3px solid var(--color-info);
  outline-offset: 2px;
}

.a11y-option.active {
  background: var(--color-primary);
  color: var(--color-white);
  border-color: var(--color-primary);
}

.a11y-option.active:hover {
  background: var(--color-primary-hover);
  border-color: var(--color-primary-hover);
}

.a11y-option-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-sm);
  background: rgba(0, 0, 0, 0.06);
  font-size: var(--font-size-base);
  font-weight: 600;
  flex-shrink: 0;
}

.a11y-option.active .a11y-option-icon {
  background: rgba(255, 255, 255, 0.2);
}

.a11y-option-label {
  flex: 1;
  font-weight: 500;
}

.a11y-option-status {
  font-size: var(--font-size-xs);
  opacity: 0.7;
}

/* Footer */
.a11y-panel-footer {
  padding: var(--spacing-md);
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  flex-shrink: 0;
}

.a11y-reset {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-error);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  transition: background var(--transition-fast);
}

.a11y-reset:hover {
  background: #dc2626;
}

.a11y-reset:focus-visible {
  outline: 3px solid var(--color-info);
  outline-offset: 2px;
}

.a11y-statement-link {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  color: var(--color-info);
  border: 1px solid var(--color-info);
  border-radius: var(--border-radius);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  transition: all var(--transition-fast);
}

.a11y-statement-link:hover {
  background: var(--color-info-light);
}

.a11y-statement-link:focus-visible {
  outline: 3px solid var(--color-info);
  outline-offset: 2px;
}

/* Slide Transition */
.a11y-slide-enter-active,
.a11y-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.a11y-slide-enter-from,
.a11y-slide-leave-to {
  transform: translateX(-100%);
}

/* Fade Transition (backdrop) */
.a11y-fade-enter-active,
.a11y-fade-leave-active {
  transition: opacity 0.3s ease;
}

.a11y-fade-enter-from,
.a11y-fade-leave-to {
  opacity: 0;
}

/* Mobile */
@media (max-width: 480px) {
  .a11y-panel {
    width: 100vw;
  }

  .a11y-trigger {
    top: auto;
    bottom: 80px;
    transform: none;
    width: 44px;
    height: 44px;
  }

  .a11y-trigger:hover {
    width: 44px;
  }
}
</style>
